import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import {
  LayoutDashboard, Users, Wallet, PlusCircle, LogOut,
  Sun, Moon, Sparkles, Menu, X, KanbanSquare, Image as ImageIcon, UserCog
} from "lucide-react";
import { useState } from "react";
import { cn } from "../lib/utils";
import WarrantyBanner from "../components/WarrantyBanner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/customers", label: "Müşteriler", icon: Users, testid: "nav-customers" },
  { to: "/kanban", label: "Kanban", icon: KanbanSquare, testid: "nav-kanban" },
  { to: "/customers/new", label: "Yeni Müşteri", icon: PlusCircle, testid: "nav-new-customer" },
  { to: "/portfolio", label: "Portföy", icon: ImageIcon, testid: "nav-portfolio" },
  { to: "/expenses", label: "Giderler", icon: Wallet, testid: "nav-expenses" },
  { to: "/profile", label: "Profil", icon: UserCog, testid: "nav-profile" },
];

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const currentTitle = NAV.find((n) => n.to === loc.pathname)?.label
    || (loc.pathname.startsWith("/customers/") ? "Müşteri Detayı" : "SwayDesign");

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 z-40 h-screen w-64 border-r border-border bg-card transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-16 flex items-center justify-between px-5 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-foreground flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-background" />
            </div>
            <div>
              <div className="text-sm font-display font-semibold tracking-tight leading-none">SwayDesign</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Tasarımcı Paneli</div>
            </div>
          </div>
          <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="p-3 sidebar-scroll overflow-y-auto h-[calc(100vh-4rem)]">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-3 py-2">Menü</div>
          <ul className="space-y-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <li key={n.to}>
                  <NavLink
                    to={n.to}
                    end={n.to === "/"}
                    data-testid={n.testid}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200",
                      isActive
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{n.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="mt-8 border-t border-border pt-4 px-3">
            <div className="text-xs text-muted-foreground truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground/70 truncate mb-3">{user?.email}</div>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn" className="w-full justify-start">
              <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
            </Button>
          </div>
        </nav>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-5 lg:px-10 border-b border-border bg-background/70 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground" onClick={() => setOpen(true)} data-testid="sidebar-open">
              <Menu className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-display font-semibold tracking-tight">{currentTitle}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Tema">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </header>

        <main className="p-5 lg:p-10 max-w-[1400px] space-y-5">
          <WarrantyBanner />
          {children}
        </main>
      </div>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}
